/**
 * Sports API - Fetch sports from backend
 * Provides dynamic sport selection across the app
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// ============================================================================
// CACHE
// ============================================================================

let cachedSports = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Get all active sports
 * Results are cached for 5 minutes
 * @param {boolean} forceRefresh - Force refresh from API
 * @returns {Promise<Array>} Array of sport objects
 */
export const getSports = async (forceRefresh = false) => {
  const now = Date.now();
  
  // Return cached data if valid
  if (!forceRefresh && cachedSports && (now - cacheTimestamp) < CACHE_DURATION) {
    return cachedSports;
  }

  try {
    const response = await fetch(`${API_URL}/api/sports`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch sports');
    }
    
    const data = await response.json();

    const sports = Array.isArray(data)
      ? data
      : Array.isArray(data?.sports)
        ? data.sports
        : Array.isArray(data?.data)
          ? data.data
          : null;
    
    if (sports && sports.length > 0) {
      cachedSports = sports;
      cacheTimestamp = now;
      return sports;
    }
    
    return cachedSports || getFallbackSports();
  } catch (error) {
    console.error('Error fetching sports:', error);
    // Return cached data if available, otherwise return fallback
    return cachedSports || getFallbackSports();
  }
};

/**
 * Get sport by slug (e.g., 'soccer', 'basketball')
 * @param {string} slug 
 * @returns {Promise<Object|null>}
 */
export const getSportBySlug = async (slug) => {
  try {
    const response = await fetch(`${API_URL}/api/sports/slug/${slug}`);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error(`Error fetching sport by slug ${slug}:`, error);
    return null;
  }
};

/**
 * Get sport by ID
 * @param {string} id 
 * @returns {Promise<Object|null>}
 */
export const getSportById = async (id) => {
  try {
    const response = await fetch(`${API_URL}/api/sports/${id}`);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error(`Error fetching sport by id ${id}:`, error);
    return null;
  }
};

/**
 * Get just sport names (for simple dropdowns/selectors)
 * @returns {Promise<string[]>}
 */
export const getSportNames = async () => {
  const sports = await getSports();
  return sports.map(s => s.name);
};

/**
 * Get positions for a specific sport
 * @param {string} sportName 
 * @returns {Promise<string[]>}
 */
export const getPositionsForSport = async (sportName) => {
  const sports = await getSports();
  const sport = sports.find(s => s.name.toLowerCase() === sportName.toLowerCase());
  return sport?.positions || [];
};

/**
 * Get game formats for a specific sport
 * @param {string} sportName 
 * @returns {Promise<string[]>}
 */
export const getGameFormatsForSport = async (sportName) => {
  const sports = await getSports();
  const sport = sports.find(s => s.name.toLowerCase() === sportName.toLowerCase());
  return sport?.gameFormats || [];
};

/**
 * Clear the sports cache
 */
export const clearSportsCache = () => {
  cachedSports = null;
  cacheTimestamp = 0;
};

// ============================================================================
// SYNCHRONOUS HELPERS (using cached data)
// ============================================================================

/**
 * Get emoji synchronously from cached data (fallback if not cached)
 * @param {string} sportName 
 * @returns {string}
 */
export const getSportEmojiSync = (sportName) => {
  if (cachedSports) {
    const sport = cachedSports.find(s => s.name.toLowerCase() === sportName?.toLowerCase());
    if (sport) return sport.emoji;
  }
  // Fallback emoji map
  const emojiMap = {
    soccer: '⚽', basketball: '🏀', baseball: '⚾', softball: '⚾',
    football: '🏈', volleyball: '🏐', tennis: '🎾', swimming: '🏊',
    hockey: '🏒', golf: '⛳', lacrosse: '🥍', wrestling: '🤼',
    gymnastics: '🤸', rugby: '🏉', cricket: '🏏', badminton: '🏸',
    'table tennis': '🏓', 'martial arts': '🥋', 'track & field': '🏃',
    'cross country': '🏃', track: '🏃',
  };
  return emojiMap[sportName?.toLowerCase()] || '🏆';
};

/**
 * Get icon synchronously from cached data
 * @param {string} sportName 
 * @returns {string}
 */
export const getSportIconSync = (sportName) => {
  if (cachedSports) {
    const sport = cachedSports.find(s => s.name.toLowerCase() === sportName?.toLowerCase());
    if (sport) return sport.icon;
  }
  return 'trophy';
};

/**
 * Get positions synchronously from cached data
 * @param {string} sportName 
 * @returns {string[]}
 */
export const getPositionsSync = (sportName) => {
  if (cachedSports) {
    const sport = cachedSports.find(s => s.name.toLowerCase() === sportName?.toLowerCase());
    if (sport) return sport.positions || [];
  }
  // Fallback positions
  const positionsMap = {
    'Soccer': ['Forward', 'Midfielder', 'Defender', 'Goalkeeper'],
    'Basketball': ['Point Guard', 'Shooting Guard', 'Small Forward', 'Power Forward', 'Center'],
    'Baseball': ['Pitcher', 'Catcher', 'Infielder', 'Outfielder'],
    'Softball': ['Pitcher', 'Catcher', 'Infielder', 'Outfielder'],
    'Football': ['Quarterback', 'Running Back', 'Wide Receiver', 'Tight End', 'Offensive Line', 'Defensive Line', 'Linebacker', 'Defensive Back', 'Special Teams'],
    'Volleyball': ['Outside Hitter', 'Opposite Hitter', 'Middle Blocker', 'Setter', 'Libero'],
    'Hockey': ['Goalie', 'Defenseman', 'Forward', 'Center', 'Wing'],
    'Lacrosse': ['Attack', 'Midfield', 'Defense', 'Goalie'],
  };
  return positionsMap[sportName] || ['Player'];
};

/**
 * Get game formats synchronously from cached data
 * @param {string} sportName 
 * @returns {string[]}
 */
export const getGameFormatsSync = (sportName) => {
  if (cachedSports) {
    const sport = cachedSports.find(s => s.name.toLowerCase() === sportName?.toLowerCase());
    if (sport) return sport.gameFormats || [];
  }
  // Fallback from constants/gameFormats.js
  const formatsMap = {
    Soccer: ['3v3', '4v4', '5v5', '6v6', '7v7', '8v8', '9v9', '11v11', 'Futsal'],
    Basketball: ['1v1', '2v2', '3v3', '4v4', '5v5'],
    Volleyball: ['2v2', '4v4', '6v6', 'Beach (2v2)'],
    Football: ['5v5', '6v6', '7v7', '8v8', '9v9', '11v11', 'Flag', 'Touch'],
    Baseball: ['9v9', 'T-Ball', 'Coach Pitch', 'Kid Pitch'],
    Tennis: ['Singles', 'Doubles', 'Mixed Doubles'],
    Swimming: ['Individual', 'Relay', 'Medley', 'Open Water'],
    Hockey: ['3v3', '4v4', '5v5', '6v6', 'Ice', 'Street', 'Field'],
    Rugby: ['7s', '10s', '15s', 'Touch', 'Tag'],
    Cricket: ['T20', 'ODI', 'Test', 'Indoor'],
    Lacrosse: ['6v6', '10v10', 'Box', 'Field'],
    Golf: ['Stroke Play', 'Match Play', 'Scramble', 'Best Ball'],
    Wrestling: ['Folkstyle', 'Freestyle', 'Greco-Roman'],
    Gymnastics: ['Artistic', 'Rhythmic', 'Trampoline', 'Acrobatic'],
    Track: ['Sprint', 'Distance', 'Field Events', 'Combined Events'],
    Other: ['Custom Format'],
  };
  return formatsMap[sportName] || ['Custom Format'];
};

// ============================================================================
// FALLBACK DATA
// ============================================================================

/**
 * Fallback sports data when API is unavailable
 */
export const getFallbackSports = () => [
  { _id: '1', name: 'Soccer', slug: 'soccer', emoji: '⚽', icon: 'football', positions: ['Forward', 'Midfielder', 'Defender', 'Goalkeeper'], gameFormats: ['3v3', '5v5', '7v7', '11v11'], isActive: true, sortOrder: 1 },
  { _id: '2', name: 'Basketball', slug: 'basketball', emoji: '🏀', icon: 'basketball', positions: ['Point Guard', 'Shooting Guard', 'Small Forward', 'Power Forward', 'Center'], gameFormats: ['3v3', '5v5'], isActive: true, sortOrder: 2 },
  { _id: '3', name: 'Baseball', slug: 'baseball', emoji: '⚾', icon: 'baseball', positions: ['Pitcher', 'Catcher', 'Infielder', 'Outfielder'], gameFormats: ['9v9'], isActive: true, sortOrder: 3 },
  { _id: '4', name: 'Softball', slug: 'softball', emoji: '⚾', icon: 'baseball', positions: ['Pitcher', 'Catcher', 'Infielder', 'Outfielder'], gameFormats: ['Fastpitch', 'Slowpitch'], isActive: true, sortOrder: 4 },
  { _id: '5', name: 'Football', slug: 'football', emoji: '🏈', icon: 'american-football', positions: ['Quarterback', 'Running Back', 'Wide Receiver', 'Defensive Line'], gameFormats: ['Flag', '11v11'], isActive: true, sortOrder: 5 },
  { _id: '6', name: 'Volleyball', slug: 'volleyball', emoji: '🏐', icon: 'baseball', positions: ['Setter', 'Libero', 'Outside Hitter'], gameFormats: ['6v6', 'Beach'], isActive: true, sortOrder: 6 },
  { _id: '7', name: 'Tennis', slug: 'tennis', emoji: '🎾', icon: 'tennisball', positions: [], gameFormats: ['Singles', 'Doubles'], isActive: true, sortOrder: 7 },
  { _id: '8', name: 'Track & Field', slug: 'track-field', emoji: '🏃', icon: 'walk', positions: [], gameFormats: ['Sprint', 'Distance'], isActive: true, sortOrder: 8 },
  { _id: '9', name: 'Swimming', slug: 'swimming', emoji: '🏊', icon: 'water', positions: [], gameFormats: ['Individual', 'Relay'], isActive: true, sortOrder: 9 },
  { _id: '10', name: 'Hockey', slug: 'hockey', emoji: '🏒', icon: 'baseball', positions: ['Goalie', 'Defenseman', 'Forward'], gameFormats: ['5v5', '6v6'], isActive: true, sortOrder: 10 },
  { _id: '11', name: 'Lacrosse', slug: 'lacrosse', emoji: '🥍', icon: 'baseball', positions: ['Attack', 'Midfield', 'Defense', 'Goalie'], gameFormats: ['6v6', '10v10'], isActive: true, sortOrder: 11 },
  { _id: '12', name: 'Wrestling', slug: 'wrestling', emoji: '🤼', icon: 'body', positions: [], gameFormats: ['Folkstyle', 'Freestyle'], isActive: true, sortOrder: 12 },
  { _id: '13', name: 'Golf', slug: 'golf', emoji: '⛳', icon: 'golf', positions: [], gameFormats: ['Stroke Play', 'Match Play'], isActive: true, sortOrder: 13 },
  { _id: '14', name: 'Cross Country', slug: 'cross-country', emoji: '🏃', icon: 'walk', positions: [], gameFormats: ['5K', '10K'], isActive: true, sortOrder: 14 },
  { _id: '15', name: 'Gymnastics', slug: 'gymnastics', emoji: '🤸', icon: 'body', positions: [], gameFormats: ['Artistic', 'Rhythmic'], isActive: true, sortOrder: 15 },
  { _id: '16', name: 'Rugby', slug: 'rugby', emoji: '🏉', icon: 'american-football', positions: [], gameFormats: ['7s', '15s'], isActive: true, sortOrder: 16 },
  { _id: '17', name: 'Cricket', slug: 'cricket', emoji: '🏏', icon: 'baseball', positions: ['Batsman', 'Bowler'], gameFormats: ['T20', 'ODI'], isActive: true, sortOrder: 17 },
  { _id: '18', name: 'Badminton', slug: 'badminton', emoji: '🏸', icon: 'tennisball', positions: [], gameFormats: ['Singles', 'Doubles'], isActive: true, sortOrder: 18 },
  { _id: '19', name: 'Table Tennis', slug: 'table-tennis', emoji: '🏓', icon: 'tennisball', positions: [], gameFormats: ['Singles', 'Doubles'], isActive: true, sortOrder: 19 },
  { _id: '20', name: 'Martial Arts', slug: 'martial-arts', emoji: '🥋', icon: 'body', positions: [], gameFormats: ['Karate', 'Taekwondo', 'Judo'], isActive: true, sortOrder: 20 },
  { _id: '21', name: 'Other', slug: 'other', emoji: '🏆', icon: 'trophy', positions: [], gameFormats: ['Custom'], isActive: true, sortOrder: 99 },
];

/**
 * Get fallback sport names list (for immediate use without API)
 */
export const getFallbackSportNames = () => getFallbackSports().map(s => s.name);

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

const sportsApi = {
  getSports,
  getSportBySlug,
  getSportById,
  getSportNames,
  getPositionsForSport,
  getGameFormatsForSport,
  getSportEmojiSync,
  getSportIconSync,
  getPositionsSync,
  getGameFormatsSync,
  clearSportsCache,
  getFallbackSports,
  getFallbackSportNames,
};

export default sportsApi;
